import { BeastMcpContext } from './types.js';

/**
 * Default implementation of BeastMcpContext
 */
export class DefaultBeastMcpContext implements BeastMcpContext {
  private sessionId: string;
  private hostId: string;
  private toolId: string;
  private logger: Console;
  private timers: Map<string, number> = new Map();
  private metrics: Map<string, number[]> = new Map();

  constructor(toolId: string, sessionId?: string, hostId?: string) {
    this.toolId = toolId;
    this.sessionId = sessionId || `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.hostId = hostId || `host-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.logger = console;
  }

  log(message: string): void {
    this.logger.log(`[${this.toolId}] ${message}`);
  }

  reportProgress(percent: number, message?: string): void {
    const progressMessage = message ? `${percent}% - ${message}` : `${percent}%`;
    this.logger.log(`[${this.toolId}] Progress: ${progressMessage}`);
  }

  getToolId(): string {
    return this.toolId;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getHostId(): string {
    return this.hostId;
  }

  /**
   * Get the logger instance for advanced logging
   */
  getLogger(): Console {
    return this.logger;
  }

  /**
   * Start a performance timer
   */
  startTimer(name: string): void {
    this.timers.set(name, Date.now());
  }

  /**
   * End a performance timer and return duration in milliseconds
   */
  endTimer(name: string): number {
    const startTime = this.timers.get(name);
    if (!startTime) {
      throw new Error(`Timer '${name}' was not started`);
    }
    
    const duration = Date.now() - startTime;
    this.timers.delete(name);
    return duration;
  }

  /**
   * Get all active timers
   */
  getTimers(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [name, startTime] of this.timers.entries()) {
      result[name] = Date.now() - startTime;
    }
    return result;
  }

  /**
   * Record a metric value
   */
  recordMetric(name: string, value: number): void {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    this.metrics.get(name)!.push(value);
  }

  /**
   * Get all recorded metrics
   */
  getMetrics(): Record<string, number[]> {
    const result: Record<string, number[]> = {};
    for (const [name, values] of this.metrics.entries()) {
      result[name] = [...values];
    }
    return result;
  }
}

/**
 * Context factory for creating context instances
 */
export class ContextFactory {
  private static sessionCounter = 0;

  /**
   * Create a new context instance
   */
  static create(toolId: string, sessionId?: string, hostId?: string): BeastMcpContext {
    return new DefaultBeastMcpContext(toolId, sessionId, hostId);
  }

  /**
   * Create a context with a unique session ID
   */
  static createWithUniqueSession(toolId: string, hostId?: string): BeastMcpContext {
    const sessionId = `session-${Date.now()}-${++this.sessionCounter}`;
    return new DefaultBeastMcpContext(toolId, sessionId, hostId);
  }
} 